import { Component } from '@angular/core';
import {
  DictionaryACTypeEntity,
  DictionaryACTypesDataService,
} from '../../../app/shared';
import { DataTableDataDefinition } from 'src/app/shared/fca-data-table';
import { DictionaryType } from 'src/components/shared/dictionaries-layout';
import { MatDialog } from '@angular/material/dialog';
import { AddNewAcTypeDialogComponent } from './add-new-ac-type-dialog/add-new-ac-type-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  standalone: false,
  selector: 'dictionaries-actypes-tab',
  templateUrl: './actypes.component.html',
  styleUrls: ['./actypes.component.scss'],
})
export class DictionaryACTypesTab {
  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private dataService: DictionaryACTypesDataService
  ) {
    this.onUpdateRecord = this.onUpdateRecord.bind(this);
  }

  pageSize = 10;
  title = 'Dictionary';
  dictionaryType = DictionaryType.AcTypes;

  data: DictionaryACTypeEntity[] | null = null;

  dataDefinition: DataTableDataDefinition<DictionaryACTypeEntity> = [
    {
      name: '',
      allowGroup: true,
      columns: [
        {
          fieldName: 'icaoType',
          caption: 'A/C Type ICAO',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if (value === undefined || value === null || value === '')
              return 'Value cannot be empty';
            const maxValueLength = 5;
            const pattern = /^[a-zA-Z0-9]+$/;
            const stringValue = value.toString();

            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only numbers and letters';
            }

            return null;
          },
        },
        {
          fieldName: 'iataType',
          caption: 'A/C Type IATA',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if (value === undefined || value === null || value === '')
              return 'Value cannot be empty';
            const maxValueLength = 5;
            const pattern = /^[a-zA-Z0-9]+$/;
            const stringValue = value.toString();

            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only numbers and letters';
            }

            return null;
          },
        },
        {
          fieldName: 'sched',
          caption: 'A/C TYPE SCHED',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            const maxValueLength = 5;
            const pattern = /^[a-zA-Z0-9]+$/;
            if (!value) {
              return null;
            }
            const stringValue = value.toString();

            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only numbers and letters';
            }

            return null;
          },
        },
        {
          fieldName: 'avgFuelConsPerBh',
          caption: 'AVG FUEL CONS PER BH',
          valueType: 'number',
          editable: true,
          validator: (value: string | undefined) => {
            const maxValueLength = 6;
            const pattern = /^[0-9]+$/;

            if (!value) {
              return null;
            }

            const stringValue = value.toString();

            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only numbers';
            }

            return null;
          },
        },
        {
          fieldName: 'tankCapacity',
          caption: 'Tank Capacity',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if (value === undefined || value === null || value === '')
              return 'Value cannot be empty';
            const maxValueLength = 6;
            return value.length > maxValueLength
              ? `Max length is ${maxValueLength}`
              : null;
          },
        },
        {
          fieldName: 'minOnBlockDiff',
          caption: 'Min On Block Diff',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if (value === undefined || value === null || value === '')
              return 'Value cannot be empty';
            const maxValueLength = 6;
            const pattern = /^-?\d{1,6}$/;
            const stringValue = value.toString();

            if (stringValue.length > maxValueLength) {
              return `Max length is ${maxValueLength}`;
            }

            if (!pattern.test(stringValue)) {
              return 'Only positive or negative integers';
            }

            return null;
          },
        },
        {
          fieldName: 'maxOnBlockDiff',
          caption: 'Max On Block Diff',
          valueType: 'text',
          editable: true,
          validator: (value: string | undefined) => {
            if (value === undefined || value === null || value === '')
              return 'Value cannot be empty';
            const maxValueLength = 6;
            return value.length > maxValueLength
              ? `Max length is ${maxValueLength}`
              : null;
          },
        },
      ],
    },
  ];

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.dataService.getAll().subscribe((receivedData) => {
      this.data = receivedData;
    });
  }

  addACType() {
    const dialogRef = this.dialog.open(AddNewAcTypeDialogComponent, {
      width: '350px',
      panelClass: 'custom-dialog-container',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe({
      next: () => {
        this.loadData();
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  onUpdateRecord(record: DictionaryACTypeEntity) {
    const updatedRecord = {
      id: record.id,
      icaoType: record.icaoType,
      iataType: record.iataType,
      tankCapacity: record.tankCapacity,
      minOnBlockDiff: record.minOnBlockDiff,
      maxOnBlockDiff: record.maxOnBlockDiff,
      sched: record.sched || null,
      avgFuelConsPerBh: record.avgFuelConsPerBh || null,
    };
    this.dataService.updateType(updatedRecord).subscribe({
      next: () => {
        this.snackBar.open('A/C Type updated.', undefined, { duration: 2000 });
        this.loadData();
      },
      error: (error) => {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '350px',
          scrollStrategy: new NoopScrollStrategy(),
          data: {
            title: 'Attention',
            message: error.error.Message,
            buttons: {
              confirm: 'Ok',
            },
          },
        });
      },
    });
  }
}
